import type { CodedOrderPayload, OrderPayloadMap } from '@/encounter/lib/order-payload';
import { patientAssessmentToCanvas } from '@/encounter/lib/from-patient-assessment';
import { toPatientAssessment, type DxCard, type PlanOrder } from '@/encounter/lib/to-patient-assessment';
import { categoryFor } from '@/encounter/lib/order-category';
import type {
  AssessmentPlan,
  AssessmentPlanMedication,
  AssessmentPlanVaccination,
  AssessmentRadiology,
  ChildMedicalCode,
  PatientAssessment,
} from '@/types/assessment-api';
import type { OrderSetItem, OrderSetItemCode, OrderSetItemType } from '@/types/order-set-api';
import type { OrderSearchPick } from '@/settings/services/use-order-set-order-search';
import type { IconName } from '@scripsteam/scrips-signal-ds';
import { ClipboardList, FileText, FlaskConical, Pill, ScanLine, Send, Syringe, type LucideIcon } from 'lucide-react';
import { isFrameV2 } from '@/shared/ui/design-v2';
import { keepSourceSystem } from '@/encounter/lib/source-system';

/**
 * An order set item's `details` (PROD-2714).
 *
 * `details` is the A/P's OWN wire entry for that order: the object the A/P save writes under
 * `childMedicalCode` (assessmentPlanMedication / radiologyOrderRequest / assessmentPlanProcedures /
 * assessmentPlanVaccination), minus its code object (that is the item's `code`) and minus what
 * belongs to one patient's visit. It is the same shape the Bundle migration (Patient PROD-2709,
 * PROD-2709-MigrateBundlesToOrderSets.sql) copies into order sets, so a migrated set and an
 * authored one read alike, and applying a set goes through the A/P's own wire mapping.
 *
 * Both directions use the A/P's own mappers, never a second copy of them:
 *   details → form   `patientAssessmentToCanvas` on a one-diagnosis, one-order document
 *   form → details   `toPatientAssessment` on the same one-diagnosis, one-order canvas
 *
 * NOT KEPT (Save-as board: "Not kept: the patient, dates, notes to the lab, order and lot numbers"):
 * `stripNotKept` removes, at every depth, the Patient service's sanitiser keys (BundleDetailsSanitizer
 * .StrippedKeys: patient/encounter/performer identity, row and order ids, status, lot and expiry,
 * interaction alerts, dates and audit stamps) plus the notes, the visit's diagnosis links and the
 * panel members another lab on that visit already had.
 */

/** The Patient service's sanitiser keys (BundleDetailsSanitizer.StrippedKeys), verbatim. */
const SANITISER_KEYS = [
  'subject', 'subjectId', 'patient', 'patientId', 'encounter', 'encounterId', 'appointmentId',
  'performer', 'performerId', 'performerDetails',
  'identifier', 'id', 'parentId', 'assessmentPlanId', 'patientRxId', 'dxId',
  'orderId', 'serviceOrderId',
  'status', 'statusReason', 'isOrderSign', 'hasOrderSent',
  'lotNo', 'lotNumber', 'expiryDate', 'expirationDate',
  'vidalAlerts', 'vidalAlertRequest',
  'onSetDate', 'endDate', 'administratedDate', 'orderDate',
  'createdBy', 'createdDate', 'updatedBy', 'updatedDate',
] as const;

/** What else a set never keeps: notes, the visit's diagnosis links, the visit's other labs. */
const VISIT_KEYS = [
  'note', 'notes', 'notePlainText', 'remarks',
  'rxDiagnosisCodesRequest', 'alreadyOrderedTests',
  // A procedure plan is a whole AssessmentPlan node; these are its diagnosis-node parts.
  'childMedicalCode', 'conditionCode', 'externalCode',
] as const;

export const NOT_KEPT_KEYS: readonly string[] = [...SANITISER_KEYS, ...VISIT_KEYS];
const NOT_KEPT = new Set(NOT_KEPT_KEYS.map((k) => k.toLowerCase()));

/** A deep copy without the not-kept keys (any depth, any case, like the server's sanitiser). */
export function stripNotKept<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => stripNotKept(v)) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (NOT_KEPT.has(k.toLowerCase())) continue;
      out[k] = stripNotKept(v);
    }
    return out as T;
  }
  return value;
}

/** The item types the A/P has an order form for, and the canvas order kind each maps to. */
const FORM_KIND = {
  lab: 'lab',
  imaging: 'imaging',
  medication: 'medication',
  procedure: 'procedure',
  vaccine: 'vaccination',
} as const satisfies Partial<Record<OrderSetItemType, CodedOrderPayload['kind']>>;

type FormItemType = keyof typeof FORM_KIND;
export type FormKind = (typeof FORM_KIND)[FormItemType];

/** Whether a pressed row has an A/P order form to open for this item type (PROD-2717). */
export function hasOrderForm(type: OrderSetItemType): type is FormItemType {
  return type in FORM_KIND;
}

const ITEM_TYPE_OF_KIND: Record<FormKind, FormItemType> = {
  lab: 'lab',
  imaging: 'imaging',
  medication: 'medication',
  procedure: 'procedure',
  vaccination: 'vaccine',
};

const DX_ID = 'order-set-dx';
const ORDER_ID = 'order-set-order';

function emptyChildMedicalCode(): ChildMedicalCode {
  return {
    assessmentPlanCondition: [],
    assessmentPlanProcedures: [],
    assessmentPlanSnomed: [],
    assessmentPlanMedication: [],
    assessmentPlanVaccination: [],
    radiologyOrderRequest: [],
  };
}

const wireCoding = (c: OrderSetItemCode) => ({
  code: c.code,
  display: c.display,
  system: c.system || null,
  Definition: null,
  description: null,
  shortName: null,
});

function isProcedureSnomed(item: OrderSetItem): boolean {
  return (item.code.system ?? '').toLowerCase() === 'snomed';
}

/**
 * The A/P order payload an item's form opens on: the item's wire entry, read back through the
 * A/P's own `patientAssessmentToCanvas`. Null for a type with no A/P order form.
 */
export function itemToPayload(item: OrderSetItem): CodedOrderPayload | null {
  if (!hasOrderForm(item.type)) return null;
  const details = stripNotKept(item.details ?? {});
  const cmc = emptyChildMedicalCode();
  switch (item.type) {
    case 'medication':
      cmc.assessmentPlanMedication.push({
        ...details,
        medicationCode: wireCoding(item.code),
      } as AssessmentPlanMedication);
      break;
    case 'lab':
    case 'imaging':
      cmc.radiologyOrderRequest.push({
        ...categoryFor(item.type),
        ...details,
        code: { code: item.code.code, displayName: item.code.display, system: item.code.system || null },
      } as AssessmentRadiology);
      break;
    case 'procedure': {
      const plan = {
        ...details,
        childMedicalCode: emptyChildMedicalCode(),
        procedureCode: isProcedureSnomed(item)
          ? null
          : { code: item.code.code, display: item.code.display, displayName: item.code.display, System: item.code.system || null },
        snomedCode: isProcedureSnomed(item) ? wireCoding(item.code) : null,
      } as unknown as AssessmentPlan;
      (isProcedureSnomed(item) ? cmc.assessmentPlanSnomed : cmc.assessmentPlanProcedures).push(plan);
      break;
    }
    case 'vaccine':
      cmc.assessmentPlanVaccination.push({
        ...details,
        vaccine: wireCoding(item.code),
      } as unknown as AssessmentPlanVaccination);
      break;
  }
  const pa = {
    identifier: null,
    assessmentPlanCondition: [
      {
        identifier: DX_ID,
        isPrimary: true,
        childMedicalCode: cmc,
        conditionCode: null,
        externalCode: null,
        isDeleted: false,
      },
    ],
  } as unknown as PatientAssessment;
  const canvas = patientAssessmentToCanvas(pa);
  const first = canvas.orders[0];
  const payload = first ? canvas.payloads.get(first.id) : undefined;
  if (!payload) return null;
  // A lab's specimen is display-only on the payload (never on the wire); a set keeps it in details.
  if (payload.kind === 'lab' && typeof details.specimen === 'string' && details.specimen) {
    return { ...payload, specimen: details.specimen };
  }
  return payload;
}

/** An item's `type`, `code` and `details` from an A/P order form's payload. */
export interface ItemFromPayload {
  type: OrderSetItemType;
  code: OrderSetItemCode;
  details: Record<string, unknown>;
}

/**
 * The item a confirmed A/P order form produces: the payload written through the A/P's own
 * `toPatientAssessment` (under a placeholder diagnosis, no patient, no encounter), then the one
 * entry it wrote, without its code and without anything not kept. Null for a referral (the set
 * builder offers no referral form).
 */
export function payloadToItem(payload: CodedOrderPayload, label: string): ItemFromPayload | null {
  if (payload.kind === 'referral') return null;
  const dx: DxCard = { id: DX_ID, display: '', isPrimary: true };
  const order: PlanOrder = { id: ORDER_ID, type: payload.kind, label, dxIds: [DX_ID] };
  const payloads: OrderPayloadMap = new Map([[ORDER_ID, payload]]);
  const pa = toPatientAssessment([dx], [order], payloads, new Map(), {
    encounter: '',
    subject: '',
    performer: '',
  });
  const cmc = pa.assessmentPlanCondition[0]?.childMedicalCode;
  if (!cmc) return null;
  const type = ITEM_TYPE_OF_KIND[payload.kind];

  const done = (code: OrderSetItemCode, entry: object, drop: string[]): ItemFromPayload => {
    const rest: Record<string, unknown> = { ...(entry as Record<string, unknown>) };
    for (const k of drop) delete rest[k];
    return { type, code, details: stripNotKept(rest) };
  };
  const coding = (c: { code?: string | null; display?: string | null; displayName?: string | null; system?: string | null; System?: string | null } | null | undefined): OrderSetItemCode => ({
    system: c?.system ?? c?.System ?? '',
    code: c?.code ?? '',
    display: c?.display ?? c?.displayName ?? label,
  });

  switch (payload.kind) {
    case 'medication': {
      const entry = cmc.assessmentPlanMedication[0];
      return entry ? done(coding(entry.medicationCode), entry, ['medicationCode']) : null;
    }
    case 'lab':
    case 'imaging': {
      const entry = cmc.radiologyOrderRequest[0];
      if (!entry) return null;
      const item = done(coding(entry.code), entry, ['code']);
      if (payload.kind === 'lab' && payload.specimen) item.details.specimen = payload.specimen;
      return item;
    }
    case 'procedure': {
      const entry = cmc.assessmentPlanProcedures[0] ?? cmc.assessmentPlanSnomed[0];
      if (!entry) return null;
      const code = entry.procedureCode
        ? coding(entry.procedureCode)
        : { ...coding(entry.snomedCode), system: entry.snomedCode?.system || 'snomed' };
      return done(code, entry, ['procedureCode', 'snomedCode']);
    }
    case 'vaccination': {
      const entry = cmc.assessmentPlanVaccination[0];
      return entry ? done(coding(entry.vaccine), entry, ['vaccine']) : null;
    }
  }
}

// ── The coding system an item keeps (PROD-2828) ──────────────────────────────────────────────

/**
 * The code and coding system a builder search pick carries: a medication's own `system` (DDC, Vidal,
 * DOH), a vaccine's `valueCoding.system`, a lab / imaging / procedure row's `system`. Blank when the
 * catalogue row has none; an article has no code.
 */
export function pickCoding(pick: OrderSearchPick): { code: string; system: string } {
  switch (pick.type) {
    case 'medication':
      return { code: pick.item.code ?? '', system: pick.item.system ?? '' };
    case 'vaccine':
      return { code: pick.item.valueCoding?.code ?? '', system: pick.item.valueCoding?.system ?? '' };
    case 'lab':
    case 'imaging':
    case 'procedure':
      return { code: pick.item.code ?? '', system: pick.item.system ?? '' };
    default:
      // Education, and any pick with no coding: blank, so withSourceSystem fills nothing.
      return { code: '', system: '' };
  }
}

/**
 * The saved item keeps the system its source carried. Some A/P order forms do not write the system
 * back onto their payload (the medication dose form drops it), so an item added from a DDC search
 * row saved `system: ""`. A blank system on the form's result takes the source's (the search pick,
 * or the row being edited) when the code is the same one. A system the form did write wins, and a
 * source with no system leaves the item as it is: nothing is guessed.
 */
export function withSourceSystem(
  result: ItemFromPayload,
  source: { code?: string | null; system?: string | null } | null | undefined,
): ItemFromPayload {
  // The rule itself lives in `@/encounter/lib/source-system`, shared with the A/P attach (PROD-2831).
  const code = keepSourceSystem(result.code, source);
  return code === result.code ? result : { ...result, code };
}

// ── The row's three lines: details, code and type tag (EditModal board) ───────────────────────

/** The short type tag on an order row. A SNOMED procedure says so rather than claiming CPT. */
export function itemTypeTag(item: Pick<OrderSetItem, 'type' | 'code'>): string {
  switch (item.type) {
    case 'lab':
      return 'LAB';
    case 'imaging':
      return 'IMG';
    case 'medication':
      return 'MED';
    case 'vaccine':
      return 'VAX';
    case 'procedure':
      return (item.code.system ?? '').toLowerCase() === 'snomed' ? 'SNOMED' : 'CPT';
    case 'education':
      return 'EDU';
    case 'referral':
      return 'REF';
    default:
      return 'OTHER';
  }
}

const SYSTEM_LABEL: Record<string, string> = {
  loinc: 'LOINC',
  cpt: 'CPT',
  cvx: 'CVX',
  snomed: 'SNOMED',
  ddc: 'DDC',
  rxnorm: 'RxNorm',
};

/**
 * The code line: "LOINC 4548-4", "CPT 92250", "CVX 150". Medications and education carry none on
 * the board (a drug's name is its identity there, and an article has no code).
 */
export function formatItemCode(item: Pick<OrderSetItem, 'type' | 'code'>): string {
  if (item.type === 'medication' || item.type === 'education') return '';
  const code = (item.code.code ?? '').trim();
  if (!code) return '';
  const raw = (item.code.system ?? '').trim();
  const system = SYSTEM_LABEL[raw.toLowerCase()] ?? raw.toUpperCase();
  return system ? `${system} ${code}` : code;
}

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '');
const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const codingText = (v: unknown): string => {
  const c = obj(v);
  return text(c.display) || text(c.displayName) || text(c.code);
};
const capitalise = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const join = (parts: string[]) => parts.filter(Boolean).join(' · ');

/** "tablet" → "tablets" for a count other than one. Units that are not plain words stay as-is. */
function unitFor(count: number, unit: string): string {
  if (count === 1 || !/^[a-z]{3,}$/.test(unit) || unit.endsWith('s')) return unit;
  return `${unit}s`;
}

const DURATION_UNIT_WORD: Record<string, [string, string]> = {
  day: ['day', 'days'],
  wk: ['week', 'weeks'],
  mo: ['month', 'months'],
};

function formatMedication(d: Record<string, unknown>): string {
  const sig = text(d.sig);
  const duration = num(d.duration);
  const words = DURATION_UNIT_WORD[text(d.durationUnit) || 'day'];
  const durationText =
    duration != null
      ? `${duration} ${words ? words[duration === 1 ? 0 : 1] : text(d.durationUnit)}`
      : '';
  const dispense = num(d.dispense);
  const dispenseText =
    dispense != null && dispense > 0
      ? `${dispense} ${unitFor(dispense, text(d.dispenseUnit))}`.trim()
      : '';
  const refill = num(d.refill);
  const refillText = refill != null && refill > 0 ? `${refill} ${refill === 1 ? 'refill' : 'refills'}` : '';
  return join([sig, durationText, dispenseText, refillText]);
}

function formatLab(d: Record<string, unknown>): string {
  return join([capitalise(text(d.priority)), text(obj(d.orderLabs).labName), text(d.specimen)]);
}

function formatImaging(d: Record<string, unknown>): string {
  return join([capitalise(text(d.priority))]);
}

function formatVaccine(d: Record<string, unknown>): string {
  const amount = num(d.administrationAmount);
  const unit = codingText(d.administrationUnit);
  const dose = amount != null ? `${amount}${unit ? ` ${unit}` : ''}` : '';
  const route = codingText(d.route);
  const doseRoute = [dose, route].filter(Boolean).join(' ');
  return join([doseRoute, codingText(d.repeatPattern)]);
}

/**
 * The details line under an order: "Routine · NRL · Whole blood", "1 tablet twice daily with
 * meals · 90 days · 180 tablets · 1 refill", "0.5 mL IM · in season", "Article · sent after
 * signing". One formatter per type, reading the item's wire `details`; a field the form did not
 * fill is left out, so an item with none shows no line.
 */
export function formatItemDetails(item: Pick<OrderSetItem, 'type' | 'details'>): string {
  const d = obj(item.details);
  switch (item.type) {
    case 'medication':
      return formatMedication(d);
    case 'lab':
      return formatLab(d);
    case 'imaging':
      return formatImaging(d);
    case 'vaccine':
      return formatVaccine(d);
    case 'education':
      return 'Article · sent after signing';
    default:
      return '';
  }
}

// ── The row's leading glyph (EditModal board; the A/P Save-as list uses the same) ─────────────

/** The row's leading glyph per type (the board's lab flask, pill, syringe, book …). */
const TYPE_ICON: Record<OrderSetItemType, IconName> = {
  lab: 'lab',
  imaging: 'radiology',
  medication: 'pill',
  vaccine: 'syringe',
  procedure: 'clipboard',
  education: 'file',
  referral: 'send',
  other: 'file',
};

/** The same map as Lucide glyphs, for the Settings screens (icon pass, 15 Oct: one library, Lucide). The DS
 *  names above stay for the encounter's own rows until its pass lands. */
const TYPE_LUCIDE: Record<OrderSetItemType, LucideIcon> = {
  lab: FlaskConical,
  imaging: ScanLine,
  medication: Pill,
  vaccine: Syringe,
  procedure: ClipboardList,
  education: FileText,
  referral: Send,
  other: FileText,
};
export function orderSetTypeLucide(type: OrderSetItemType): LucideIcon {
  return TYPE_LUCIDE[type] ?? FileText;
}

/**
 * PROD-2743 design sheet §2.11 (R6 O7): ON, Education reads `bookmark` (§3.2), not the generic file.
 * Procedure stays `clipboard` (K3/O8 GAP: no procedure glyph in the DS yet; logged, not changed).
 * OFF = today's map, unchanged.
 */
export function orderSetTypeIcon(type: OrderSetItemType): IconName {
  if (isFrameV2() && type === 'education') return 'bookmark';
  return TYPE_ICON[type] ?? 'file';
}
